import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useReferenceCreateReason,
  useReferenceListReasons,
  useReferenceUpdateReason,
} from '@/api/generated/endpoints/reference/reference';
import type {
  Disposition,
  ReferenceListReasonsStatus,
  ReturnReasonCreate,
  ReturnReasonOut,
} from '@/api/generated/model';
import { problemMessage } from '@/i18n/problem';
import { reasonLabel } from '@/i18n/reason';
import { applyProblem } from '@/lib/problemForm';
import { invalidatePath } from '@/lib/queryCache';
import { statusOptions } from '@/pages/catalog/statusOptions';
import { useIsMobile } from '@/lib/useIsMobile';

type ReasonForm = ReturnReasonCreate & { is_active?: boolean };

const CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,31}$/; // как ReasonCode на бэкенде

/** Справочник причин возврата и частичной приёмки (раздел менеджера). */
export function ReturnReasonsPage() {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [status, setStatus] = useState<ReferenceListReasonsStatus>('all');
  const [editing, setEditing] = useState<ReturnReasonOut | 'new' | null>(null);
  const list = useReferenceListReasons({ status });

  const columns: TableColumnsType<ReturnReasonOut> = [
    { key: 'code', title: t('reasons.colCode'), dataIndex: 'code', width: 170 },
    {
      key: 'label',
      title: t('reasons.colLabel'),
      render: (_: unknown, r) => (
        <Space>
          {reasonLabel(r)}
          {!r.is_active && <Tag>{t('common.archived')}</Tag>}
        </Space>
      ),
    },
    {
      key: 'disposition',
      title: t('reasons.colDisposition'),
      dataIndex: 'default_disposition',
      width: 190,
      render: (d: Disposition) => (
        <Tag color={d === 'restock' ? 'green' : 'red'}>{t(`disposition.${d}`)}</Tag>
      ),
    },
    { key: 'sort_order', title: t('reasons.colOrder'), dataIndex: 'sort_order', width: 100 },
    {
      key: 'actions',
      width: 50,
      render: (_: unknown, r) => (
        <Button
          type="text"
          icon={<EditOutlined />}
          aria-label={t('common.edit')}
          onClick={() => {
            setEditing(r);
          }}
        />
      ),
    },
  ];

  // Телефон (T-177): причина — одной ячейкой: название, судьба товара, код; кнопки — отдельной узкой колонкой.
  const mobileColumns: TableColumnsType<ReturnReasonOut> = [
    {
      key: 'label',
      title: t('reasons.colLabel'),
      render: (_: unknown, r) => (
        <Space direction="vertical" size={2}>
          <Space size={4} wrap>
            {reasonLabel(r)}
            {!r.is_active && <Tag>{t('common.archived')}</Tag>}
          </Space>
          <Space size={4} wrap>
            <Tag color={r.default_disposition === 'restock' ? 'green' : 'red'}>
              {t(`disposition.${r.default_disposition}`)}
            </Tag>
            <Typography.Text type="secondary">{r.code}</Typography.Text>
          </Space>
        </Space>
      ),
    },
    ...columns.filter((c) => c.key === 'actions'),
  ];

  return (
    <Card
      title={t('reasons.title')}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditing('new');
          }}
        >
          {t('reasons.add')}
        </Button>
      }
    >
      <Typography.Paragraph type="secondary">{t('reasons.hint')}</Typography.Paragraph>
      <Segmented
        style={{ marginBottom: 12 }}
        options={statusOptions(t)}
        value={status}
        onChange={(v) => {
          setStatus(v as ReferenceListReasonsStatus);
        }}
      />
      <Table<ReturnReasonOut>
        rowKey="id"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={list.data}
        loading={list.isFetching}
        pagination={false}
      />
      <ReasonModal
        reason={editing}
        onClose={() => {
          setEditing(null);
        }}
      />
    </Card>
  );
}

function ReasonModal({
  reason,
  onClose,
}: {
  reason: ReturnReasonOut | 'new' | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [form] = Form.useForm<ReasonForm>();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const isNew = reason === 'new';
  const handlers = {
    onSuccess: async () => {
      await invalidatePath(queryClient, '/api/v1/return-reasons');
      onClose();
    },
    onError: (e: unknown) => {
      if (!applyProblem(form, e, { reason_exists: 'code' })) setError(problemMessage(e));
    },
  };
  const create = useReferenceCreateReason({ mutation: handlers });
  const update = useReferenceUpdateReason({ mutation: handlers });

  return (
    <Modal
      title={isNew ? t('reasons.new') : t('reasons.edit')}
      open={reason !== null}
      onCancel={() => {
        setError(null);
        onClose();
      }}
      okText={t('common.save')}
      okButtonProps={{ loading: create.isPending || update.isPending }}
      onOk={() => {
        form.submit();
      }}
      destroyOnHidden
    >
      <Form<ReasonForm>
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={
          reason && reason !== 'new'
            ? {
                code: reason.code,
                label: reason.label,
                default_disposition: reason.default_disposition,
                sort_order: reason.sort_order,
                is_active: reason.is_active,
              }
            : { default_disposition: 'writeoff', sort_order: 100 }
        }
        onFinish={(v) => {
          setError(null);
          const common = {
            label: v.label,
            default_disposition: v.default_disposition,
            ...(v.sort_order === undefined ? {} : { sort_order: v.sort_order }),
          };
          if (isNew) {
            create.mutate({ data: { ...common, code: v.code } });
          } else if (reason) {
            // код не меняется: на него ссылаются отчёты
            update.mutate({
              reasonId: reason.id,
              data: { ...common, ...(v.is_active === undefined ? {} : { is_active: v.is_active }) },
            });
          }
        }}
      >
        <Form.Item
          name="code"
          label={t('reasons.code')}
          extra={t('reasons.codeHint')}
          rules={[
            { required: true, message: t('reasons.codeRequired') },
            { pattern: CODE_PATTERN, message: t('reasons.codeFormat') },
          ]}
        >
          <Input disabled={!isNew} maxLength={32} autoFocus={isNew} />
        </Form.Item>
        <Form.Item
          name="label"
          label={t('reasons.label')}
          rules={[{ required: true, whitespace: true, message: t('reasons.labelRequired') }]}
        >
          <Input maxLength={200} />
        </Form.Item>
        <Form.Item
          name="default_disposition"
          label={t('reasons.disposition')}
          extra={t('reasons.dispositionHint')}
        >
          <Radio.Group
            optionType="button"
            options={[
              { label: t('disposition.restock'), value: 'restock' },
              { label: t('disposition.writeoff'), value: 'writeoff' },
            ]}
          />
        </Form.Item>
        <Space size="large">
          <Form.Item name="sort_order" label={t('reasons.order')}>
            <InputNumber min={0} max={32767} precision={0} />
          </Form.Item>
          {!isNew && (
            <Form.Item name="is_active" label={t('common.active')} valuePropName="checked">
              <Switch />
            </Form.Item>
          )}
        </Space>
      </Form>
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}
